import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock } from "lucide-react";
import SoccerBall from "@/components/SoccerBall";

// Phase 1c — safe same-origin path validator, mirrors backend
// /utils/redirect.py::safe_return_to. Rejects absolute URLs and any path
// that could exit the admin surface.
function safeNext(candidate) {
  if (typeof candidate !== "string" || !candidate) return "/admin";
  if (candidate.length > 512) return "/admin";
  if (!candidate.startsWith("/")) return "/admin";
  if (candidate.startsWith("//")) return "/admin";
  if (candidate.includes("\\")) return "/admin";
  if (/^\/*[A-Za-z][A-Za-z0-9+.\-]*:/.test(candidate)) return "/admin";
  return candidate;
}

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const nextParam = safeNext(params.get("next"));
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const ok = await login(form.email, form.password);
    setBusy(false);
    if (ok) nav(nextParam);
    else setErr("Invalid email or password.");
  };

  return (
    <div className="container-gm py-16 md:py-24 flex justify-center" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-[var(--panini-yellow)] border-2 border-[var(--ink)] flex items-center justify-center">
            <SoccerBall size={30} strokeColor="#0A0A0A" />
          </div>
          <div className="kicker kicker-red mt-6">Owner Access</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Sign in</h1>
        </div>

        <form onSubmit={submit} className="mt-10 card-fun p-7 space-y-4" data-testid="admin-login-form">
          <label className="block">
            <div className="kicker kicker-black mb-2 text-[11px]">Email</div>
            <input
              required type="email" autoComplete="email"
              data-testid="admin-email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="gm-field"
            />
          </label>
          <label className="block">
            <div className="kicker kicker-black mb-2 text-[11px]">Password</div>
            <input
              required type="password" autoComplete="current-password"
              data-testid="admin-password"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="gm-field"
            />
          </label>
          {err && <div className="text-[var(--panini-red)] text-sm font-semibold" data-testid="admin-error">{err}</div>}
          <button
            type="submit" disabled={busy}
            data-testid="admin-login-submit"
            className="btn-red w-full justify-center disabled:opacity-60"
          >
            <Lock size={16} /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
